import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { AgentStep, ReportLine } from "../runner/types";

export const reconcileStep: AgentStep = {
    id: "reconcile",
    async run(ctx, io) {
        // CREDITOR sources are ER records, DEBITOR sources are Deb records
        const sourceOf = (a: GQL.IReconciliationAccount) =>
            a.kind === GQL.IReconciliationAccountKind.Creditor ? GQL.IProductKey.Er : GQL.IProductKey.Deb;
        if (!ctx.canWrite(GQL.IProductKey.Er) && !ctx.canWrite(GQL.IProductKey.Deb)) {
            io.narrateSkipped(GQL.IProductKey.Er);
            io.narrateSkipped(GQL.IProductKey.Deb);
            return { status: "skipped" };
        }
        const open0 = (await ctx.readReconciliationAccounts()).filter((a) => a.openCount > 0);
        const accounts = open0.filter((a) => ctx.canWrite(sourceOf(a)));
        for (const a of open0) if (!ctx.canWrite(sourceOf(a))) io.narrateSkipped(sourceOf(a));
        if (accounts.length === 0) return { status: open0.length > 0 ? "skipped" : "nothing" };

        let applied = 0;
        let skipped = 0;
        let changed = 0;
        let failed = 0;
        let accountsFailed = 0;
        for (const account of accounts) {
            if (io.isCancelled()) break;
            let plan: GQL.IReconciliationPlan;
            let results: GQL.IReconciliationGroupResult[];
            try {
                // the konto goes back to the server exactly as the accounts query returned it
                plan = await withThinking(io, "app.agent.reconcile.thinking", () =>
                    ctx.planReconciliation(account.konto),
                );
                skipped += plan.skipped.length;
                if (plan.groups.length === 0) continue;
                results = await ctx.applyReconciliation(
                    account.konto,
                    plan.groups.map((g) => String(g.groupId)),
                );
            } catch (error) {
                // a missing Bank or Kasse write right refuses the whole account; the other accounts still run
                console.error(`Agent reconcile failed for account ${account.konto}`, error);
                accountsFailed += 1;
                continue;
            }
            const count = (outcome: GQL.IReconciliationGroupOutcome) =>
                results.filter((r) => r.outcome === outcome).length;
            applied += count(GQL.IReconciliationGroupOutcome.Applied);
            changed += count(GQL.IReconciliationGroupOutcome.Changed);
            failed += count(GQL.IReconciliationGroupOutcome.Failed);
        }

        const summary: ReportLine[] = [];
        const open: ReportLine[] = [];
        if (applied > 0) {
            io.post({ kind: "done", textId: "app.agent.reconcile.done", values: { count: applied } });
            summary.push({ textId: "app.agent.report.reconciled", values: { count: applied } });
        }
        if (skipped > 0) open.push({ textId: "app.agent.report.reconcileSkipped", values: { count: skipped } });
        if (changed > 0) open.push({ textId: "app.agent.report.reconcileChanged", values: { count: changed } });
        if (failed > 0) open.push({ textId: "app.agent.report.reconcileFailed", values: { count: failed } });
        if (accountsFailed > 0) {
            open.push({ textId: "app.agent.report.reconcileAccountFailed", values: { count: accountsFailed } });
        }
        return { status: summary.length > 0 || open.length > 0 ? "done" : "nothing", summary, open };
    },
};
